(() => {
  'use strict';
  if (window.__nanitAttributionLoaded || window.Shopify?.shop !== 'frn7fj-5t.myshopify.com') return;
  window.__nanitAttributionLoaded = true;
  const storageKey = 'nanit-attribution-v1';
  const fields = ['source', 'medium', 'campaign', 'campaign_id', 'entry_path', 'referrer_host', 'basis', 'captured_at'];
  const prefix = '__nanit_';
  const idleLimit = 30 * 60 * 1000;
  let privacy;
  let record;
  let pending = Promise.resolve();
  let generation = 0;
  let lastSent = '';
  let deniedCleared = false;
  const root = window.Shopify.routes?.root || '/';

  function clean(value) {
    const text = String(value || '').trim();
    // Campaign labels only: never store contact details or full URLs.
    if (/@|[<>\u0000-\u001f]/.test(text)) return '';
    return text.slice(0, 160);
  }
  function safePath(value) {
    if (/\/(checkouts?|account|orders)\b/.test(value)) return '[restricted path]';
    return clean(value).slice(0, 200);
  }
  function url(value) { try { return new URL(value); } catch { return null; } }
  function sameStore(host) { return ['nanit.ae', 'www.nanit.ae', 'frn7fj-5t.myshopify.com'].includes(host); }
  function permitted() { return !!privacy?.analyticsProcessingAllowed() && !!privacy?.marketingAllowed(); }

  function tagged(u, basis) {
    if (!u) return null;
    const source = clean(u.searchParams.get('utm_source'));
    if (source) return {
      source,
      medium: clean(u.searchParams.get('utm_medium')) || '(not provided)',
      campaign: clean(u.searchParams.get('utm_campaign')),
      campaign_id: clean(u.searchParams.get('utm_id')),
      entry_path: safePath(u.pathname),
      referrer_host: '',
      basis
    };
    // Presence identifies the platform, not a particular campaign. IDs are not retained.
    if (['gclid', 'gbraid', 'wbraid'].some(k => u.searchParams.has(k))) return {
      source: 'google', medium: 'cpc', campaign: '', campaign_id: '',
      entry_path: safePath(u.pathname), referrer_host: '', basis: 'Google ad click identifier present'
    };
    return null;
  }

  function observedSource() {
    const here = url(location.href);
    const ref = url(document.referrer);
    const current = tagged(here, 'UTM parameters on this visit');
    if (current) return current;
    // A same-store referrer may retain the tagged entry page after delayed consent.
    if (ref && sameStore(ref.hostname)) return tagged(ref, 'UTM parameters on preceding store page');
    if (ref) {
      const host = ref.hostname.toLowerCase().replace(/^www\./, '');
      const google = /(^|\.)google\.[a-z.]+$/.test(host);
      const bing = /(^|\.)bing\.com$/.test(host);
      return {source: google ? 'google' : bing ? 'bing' : host,
        medium: google || bing ? 'organic' : 'referral', campaign: '', campaign_id: '',
        entry_path: safePath(here.pathname), referrer_host: host, basis: 'External browser referrer'};
    }
    return null;
  }

  function saved() {
    try {
      const previous = JSON.parse(sessionStorage.getItem(storageKey));
      if (previous?.record && previous.seen > Date.now() - idleLimit && previous.seen <= Date.now()) {
        const safe = Object.fromEntries(fields.map(key => [key, clean(previous.record[key])]));
        if (safe.source && safe.basis) return safe;
      }
    } catch { /* Storage can be unavailable in private browsers. */ }
    return null;
  }
  function remember() {
    try { sessionStorage.setItem(storageKey, JSON.stringify({record, seen: Date.now()})); } catch { /* Optional. */ }
  }
  function attributes(value) {
    return Object.fromEntries(fields.map(key => [prefix + key, value ? value[key] || '' : '']));
  }

  function write(value, clear = false) {
    const expectedGeneration = generation;
    const data = attributes(value);
    const fingerprint = JSON.stringify(data);
    if (!clear && fingerprint === lastSent) return pending;
    pending = pending.catch(() => {}).then(async () => {
      // Recheck immediately before the request; a consent withdrawal invalidates queued writes.
      if (!clear && (expectedGeneration !== generation || !permitted())) return;
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: {'Content-Type': 'application/json'}, body: JSON.stringify({attributes: data}), keepalive: true
      });
      if (response.ok) lastSent = fingerprint;
    }).catch(() => { /* Attribution must never prevent a purchase. Retry on the next page. */ });
    return pending;
  }

  function sync() {
    if (!privacy) return;
    if (!permitted()) {
      generation++;
      record = null;
      try { sessionStorage.removeItem(storageKey); } catch { /* Optional. */ }
      document.querySelectorAll('input[data-nanit-attribution]').forEach(input => input.remove());
      const choice = privacy.currentVisitorConsent();
      if (!deniedCleared && (choice.analytics === 'no' || choice.marketing === 'no')) {
        deniedCleared = true;
        write(null, true); // Delete our own metadata only; leave all other cart data intact.
      }
      return;
    }
    deniedCleared = false;
    const previous = saved();
    const candidate = observedSource();
    if (candidate && (!previous || ['source', 'medium', 'campaign', 'campaign_id'].some(k => candidate[k] !== previous[k]))) {
      record = {...candidate, captured_at: new Date().toISOString()};
    } else {
      record = previous || {
        source: '(unknown)', medium: '(unknown)', campaign: '', campaign_id: '',
        entry_path: safePath(location.pathname), referrer_host: '',
        basis: document.referrer ? 'No source available when consent was given' : 'No referrer or campaign tag (direct or unknown)',
        captured_at: new Date().toISOString()
      };
    }
    remember();
    write(record);
  }

  // Native cart forms carry the same fields even if the asynchronous write is still finishing.
  document.addEventListener('submit', event => {
    if (!permitted() || !record || !(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    const action = new URL(form.action, location.href);
    if (action.origin !== location.origin || !/\/cart\/?$/.test(action.pathname)) return;
    for (const [key, value] of Object.entries(attributes(record))) {
      let input = form.querySelector('input[data-nanit-attribution="' + key + '"]');
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'attributes[' + key + ']'; input.dataset.nanitAttribution = key; form.appendChild(input); }
      input.value = value;
    }
  }, true);
  document.addEventListener('visitorConsentCollected', sync);
  window.addEventListener('pageshow', event => { if (event.persisted && privacy) sync(); });
  function ready(error) { if (!error && window.Shopify?.customerPrivacy) { privacy = window.Shopify.customerPrivacy; sync(); } }
  if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') ready();
  else window.Shopify?.loadFeatures?.([{name: 'consent-tracking-api', version: '0.1'}], ready);
})();
